import { Dispatch } from 'react';
import { FormikHelpers } from 'formik';
import Bugsnag from '@bugsnag/js';

import { actions } from 'checkout/state/checkoutActions';
import { sharedValidationsMap } from 'shared/utils/validations';
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
  ITabChangeAction,
} from 'checkout/types/ActionPayload';
import { IAddress } from 'checkout/types/Address';
import { IOrder, IPartialOrder } from 'checkout/types/Order';
import { requestService } from 'shared/utils/services/requestService';
import { isUSA } from 'checkout/utils/orderUtils';
import { t } from 'localization/utils/t';

const TP = 'checkout.hooks.useShippingAddresses';

interface IUseShippingAddressesProps {
  addresses: { [id: number]: IAddress };
  dispatch: Dispatch<IAction | IErrorAction | IOrderSuccessAction | ITabChangeAction>;
  order?: IPartialOrder;
}

interface IUseShippingAddresses {
  changeShippingServiceLevel: (
    shippingServiceLevel: string,
    expressPayOrderNumber?: number,
  ) => Promise<void>;
  connectAddressToOrder: (addressId: number, newOrderNumber?: number) => Promise<void>;
  createShippingAddress: (
    address: IAddress,
    formikActions?: FormikHelpers<IAddress>,
    expressPayOrderNumber?: number,
  ) => Promise<IAddress | void>;
  createShippingAddressWithoutOrder: (
    address: IAddress,
    formikActions?: FormikHelpers<IAddress>,
  ) => Promise<IAddress | void>;
  deleteAddress: (addressId: number) => Promise<void>;
}

export const useShippingAddresses = ({
  addresses,
  dispatch,
  order,
}: IUseShippingAddressesProps): IUseShippingAddresses => {
  const orderNumber = order?.number;
  const addressErrorFieldMap = sharedValidationsMap.addressErrorFieldMap();

  const changeShippingServiceLevel = async (
    shippingServiceLevel: string,
    expressPayOrderNumber?: number,
  ) => {
    const orderNumberToConnect = orderNumber || expressPayOrderNumber;

    if (shippingServiceLevel === order?.shippingServiceLevel) {
      dispatch({
        type: actions.updateShippingServiceLevelSuccess,
        payload: order as IOrder,
      });
      return;
    }

    dispatch({ type: actions.updateShippingServiceLevelRequest });

    if (!orderNumberToConnect) {
      dispatch({
        type: actions.updateShippingServiceLevelFailure,
        payload: t(
          `${TP}.noOrderNumberToConnect`,
          'There was an issue storing your order, please return to the product page.',
        ),
      });

      const error = new Error('No order number when changing shipping service level');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null });
      });

      return;
    }

    if (!shippingServiceLevel) {
      dispatch({
        type: actions.updateShippingServiceLevelFailure,
        payload: t(
          `${TP}.noShippingServiceLevel`,
          'There was an issue selecting shipping service level. Please return to the shipping address tab.',
        ),
      });

      const error = new Error('No shipping service level selected');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null });
      });

      return;
    }

    try {
      const response = await requestService({
        url: `/web-api/v1/orders/${orderNumberToConnect}`,
        method: 'PUT',
        data: JSON.stringify({
          order: { shippingServiceLevel },
        }),
      });

      dispatch({
        type: actions.updateShippingServiceLevelSuccess,
        payload: response,
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: null });
        });

        return;
      }

      dispatch({
        type: actions.updateShippingServiceLevelFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null });
      });
    }
  };

  /**
   * NOTE: newOrderNumber is used in scenarios such as guest checkout or express pay
   * which are times when the order is not generated when the user first lands on
   * checkout but rather at a different time
   * TODO: a better design perhaps would be to extract out these hooks out of
   * the checkout context provider and have components call these hooks directly
   * and it will be the components responsibility to pass in the order object along
   * with any other necessary params
   */
  const connectAddressToOrder = async (addressId: number, newOrderNumber?: number) => {
    dispatch({ type: actions.connectAddressRequest });

    const orderNumberToConnect = orderNumber || newOrderNumber;

    try {
      if (!addressId) {
        const error = new Error(
          'Unable to select address, please try a different address or add a new one.',
        );

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        throw error;
      }

      if (!orderNumberToConnect) {
        const error = new Error(
          'There was an issue storing your order, please return to the product page.',
        );

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: null });
        });

        throw error;
      }

      const response = await requestService({
        url: `/web-api/v1/orders/${orderNumberToConnect}`,
        method: 'PUT',
        data: JSON.stringify({
          order: { addressId },
        }),
      });

      if (
        (response.landedCostChanged && !order.landedCostChanged) ||
        (response.landedCostChanged &&
          addresses[response.addressId].countryCode !== addresses[order.addressId].countryCode)
      ) {
        response.displayInternationalFeeWarning = true;
      }

      dispatch({
        type: actions.connectAddressSuccess,
        payload: response,
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });
      } else {
        dispatch({
          type: actions.connectAddressFailure,
          payload: error.message,
        });
      }

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });

      throw error;
    }
  };

  const createShippingAddressWithoutOrder = async (
    address: IAddress,
    formikActions?: FormikHelpers<IAddress>,
  ) => {
    dispatch({ type: actions.createAddressRequest });

    if (!address?.phone) {
      delete address.phone;
    }

    try {
      const international = !isUSA(address.countryCode) ? '_international' : '';

      const shippingAddress = await requestService({
        url: `/web-api/v1/addresses/shipping${international}`,
        method: 'post',
        data: JSON.stringify({
          address: {
            ...address,
            addressType: 'shipping',
          },
        }),
      });

      dispatch({ type: actions.createAddressSuccess, payload: shippingAddress });

      return shippingAddress;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        return;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      formikActions?.setSubmitting(false);

      if (addressErrorFieldMap.has(errorMessage)) {
        formikActions?.setErrors({
          [addressErrorFieldMap.get(errorMessage)]: errorMessage,
        });

        dispatch({
          type: actions.createAddressFailure,
          payload: t(`${TP}.invalidFields`, 'Please update the fields below'),
        });
      } else {
        dispatch({
          type: actions.createAddressFailure,
          payload: errorMessage,
        });
      }

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }
  };

  const createShippingAddress = async (
    address: IAddress,
    formikActions?: FormikHelpers<IAddress>,
    expressPayOrderNumber?: number,
  ) => {
    const orderNumberToConnect = orderNumber || expressPayOrderNumber;

    if (!orderNumberToConnect) {
      dispatch({
        type: actions.createAddressFailure,
        payload: t(
          `${TP}.noOrderNumberToConnect`,
          'There was an issue storing your order, please return to the product page.',
        ),
      });

      const error = new Error('No order number when creating new address');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null });
      });

      return;
    }

    dispatch({ type: actions.createAddressRequest });

    if (!address?.phone) {
      delete address.phone;
    }

    try {
      const international = !isUSA(address.countryCode) ? '_international' : '';

      const shippingAddress = await requestService({
        url: `/web-api/v1/addresses/shipping${international}`,
        method: 'post',
        data: JSON.stringify({
          address: { ...address, addressType: 'shipping' },
        }),
      });

      dispatch({ type: actions.createAddressSuccess, payload: shippingAddress });

      await connectAddressToOrder(shippingAddress.id, orderNumberToConnect);

      return shippingAddress;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: orderNumberToConnect });
        });

        return;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      formikActions?.setSubmitting(false);

      if (addressErrorFieldMap.has(errorMessage)) {
        formikActions?.setErrors({
          [addressErrorFieldMap.get(errorMessage)]: errorMessage,
        });

        dispatch({
          type: actions.createAddressFailure,
          payload: t(`${TP}.invalidFields`, 'Please update the fields below'),
        });
      } else {
        dispatch({
          type: actions.createAddressFailure,
          payload: errorMessage,
        });
      }

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: orderNumberToConnect });
      });
    }
  };

  const deleteAddress = async (addressId: number) => {
    if (!addressId) {
      dispatch({
        type: actions.deleteAddressFailure,
        payload: t(
          `${TP}.deleteAddressFailure`,
          'There was an issue deleting your address, please try again.',
        ),
      });

      const error = new Error('No address id when deleting address');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });

      return;
    }

    dispatch({ type: actions.deleteAddressRequest });

    try {
      const address = await requestService({
        url: `/web-api/v1/addresses/${addressId}`,
        method: 'delete',
      });

      dispatch({ type: actions.deleteAddressSuccess, payload: address });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        return;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      dispatch({
        type: actions.deleteAddressFailure,
        payload: errorMessage,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }
  };

  return {
    changeShippingServiceLevel,
    connectAddressToOrder,
    createShippingAddress,
    createShippingAddressWithoutOrder,
    deleteAddress,
  };
};
